import React, { useEffect, useRef, useState } from "react";
import gpayImage from "./../../assets/images/gpay.svg";
import phonepeImage from "./../../assets/images/phonepe.svg";
import paytmImage from "./../../assets/images/paytm.svg";
import { PaymentDetails } from "../../models/PaymentDetails";
import { toast } from "react-toastify";
import API from "../../services";
import axios from "axios";
import { FaCopy } from "react-icons/fa";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import { RootState } from "../../models/RootState";
import { connect } from "react-redux";
import OCR_API from "../../services/ocr";
import { useTranslation } from "../../hooks/useTranslation";
import { UserDetails } from "../../models/UserDetails";
interface PaymentDialogProps {
  paymentProps: PaymentDetails;
  closeDialog: () => void;
  activeWallet: string
  selectedPG: any
  userDetails: UserDetails;
  dynamicTextColors?: any;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ paymentProps, closeDialog, activeWallet, selectedPG, userDetails, dynamicTextColors }) => {
  const { t } = useTranslation();
  const qrCodes = Array.isArray(paymentProps.qrCode)
    ? paymentProps.qrCode.filter((qr) => typeof qr === "string" && qr.trim() !== "")
    : (paymentProps.qrCode ? [paymentProps.qrCode] : []);
  const hasQrCodes = qrCodes.length > 0;
  const [activeTab, setActiveTab] = useState<string>(() =>
    hasQrCodes ? "qrPayment" : paymentProps.isUpi ? "upiPayment" : "bankTransfer"
  );
  const [activeQrIndex, setActiveQrIndex] = useState<number>(0);
  const qrScrollRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [gpayLink, setGpayLink] = useState<string>("");
  const [phonepeLink, setPhonepeLink] = useState<string>("");
  const [paytmLink, setPaytmLink] = useState<string>("");
  const [utrNumber, setUtrNumber] = useState<string>("");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [ocrLoading, setOcrLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [preview, setPreview] = useState<string>("");

  // Get dynamic text color classes from Redux instead of calling function
  const pageTextColorClass = dynamicTextColors?.pageTextColorClass || '';
  const inputBoxTextColorClass = dynamicTextColors?.inputBoxTextColorClass || '';
  const buttonTextColorClass = dynamicTextColors?.buttonTextColorClass || '';

  // tabTextColorClass uses same as buttonTextColorClass
  const tabTextColorClass = buttonTextColorClass;
  
  // inputTextColorClass uses same as inputBoxTextColorClass
  const inputTextColorClass = inputBoxTextColorClass;

  // Validate UTR number - must be exactly 12 digits
  const isValidUtr = utrNumber.length === 12 && /^\d{12}$/.test(utrNumber);
  const ids = paymentProps.upiIds?.length ? paymentProps.upiIds : (paymentProps.upiId ? [paymentProps.upiId] : []);

  const handleUtrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // For number inputs, we need to handle the length restriction differently
    // Convert to string and ensure it's only digits, then limit to 12 characters
    const numericValue = value.toString().replace(/\D/g, '').slice(0, 12);
    setUtrNumber(numericValue);
  };

  const extractUtrFromOcr = async (file: File, depositType: string) => {
    setOcrLoading(true);
    try {
      let token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      if (!token.startsWith("Bearer ")) {
        token = `Bearer ${token}`;
      }

      const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.includes(",") ? result.split(",")[1] : result;
            resolve(base64);
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      };

      const paymentMethod = depositType === "BANK_TRANSFER" ? "BANK_TRANSFER" : "UPI";
      const imageBase64 = await fileToBase64(file);

      const formData = new FormData();
      formData.append('image', imageBase64);
      formData.append('image_names', file.name);
      formData.append('image_types', file.type);
      formData.append('request', JSON.stringify({ payment_method: paymentMethod }));

      const response = await OCR_API.post(
        '/ocr/get-utr',
        formData,
      );

      let rawUtr: any = null;
      let extractedImageUrl: string = "";

      if (typeof response.data === "string") {
        rawUtr = response.data;
      }
      else if (response.data && typeof response.data === "object") {
        if ((response.data as any).utr) {
          rawUtr = (response.data as any).utr;
        }
        if ((response.data as any).image_url) {
          extractedImageUrl = (response.data as any).image_url;
        }
        else if ((response.data as any).imageUrl) {
          extractedImageUrl = (response.data as any).imageUrl;
        }
      }

      if (extractedImageUrl) {
        setImageUrl(extractedImageUrl);
      }

      if (rawUtr != null) {
        const extractedUtr = rawUtr.toString().replace(/\D/g, '').slice(0, 12);
        if (extractedUtr.length > 0) {
          setUtrNumber(extractedUtr);
          toast.success(t("wallet.messages.utrExtractedSuccessfully"));
        } else {
          toast.warning(t("wallet.messages.couldNotExtractUtr"));
        }
      } else {
        toast.warning(t("wallet.messages.couldNotExtractUtr"));
      }
    } catch (error: any) {
      console.error("OCR API Error:", error);
      toast.error(error.response?.data?.message || t("wallet.messages.failedToExtractUtr"));
    } finally {
      setOcrLoading(false);
    }
  };

  const handleScreenshotChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        // Clean up previous preview URL if exists
        if (preview) {
          URL.revokeObjectURL(preview);
        }
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setPreview(previewUrl);
        setScreenshotFile(file);
        // toast.success("Image uploaded successfully");
        const depositType = activeTab === "bankTransfer" ? "BANK_TRANSFER" : "UPI";
        await extractUtrFromOcr(file, depositType);
      } else {
        toast.error(t("wallet.messages.pleaseUploadImageFile"));
      }
    }
  };

  const handleRemoveScreenshot = () => {
    // Clean up preview URL
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview("");
    setScreenshotFile(null);
    setUtrNumber(""); 
    setImageUrl(""); 
    const fileInput = document.getElementById('screenshotUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  useEffect(() => {
    setGpayLink(`tez://upi/${paymentProps.upiIntent}`);
    setPhonepeLink(`phonepe://${paymentProps.upiIntent}`);
    setPaytmLink(`paytmmp://${paymentProps.upiIntent}`);

    if (activeTab === "qrPayment" && !hasQrCodes) {
      // If QR is not available, try UPI, otherwise bank transfer
      if (paymentProps.isUpi) {
        setActiveTab("upiPayment");
      } else {
        setActiveTab("bankTransfer");
      }
    }
    setActiveQrIndex(0);
    if (qrScrollRef.current) {
      qrScrollRef.current.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [paymentProps]);

  // Cleanup preview URL on component unmount
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleSubmit = async (depositType: string) => {
    setLoading(true);

    try {
      const response = await API.post(`/account/processDeposit`, {
        uKey: sessionStorage.getItem("userKey"),
        depositType: depositType,
        bankId: paymentProps.bankId,
        refId: paymentProps.referenceId,
        utr: utrNumber,
        imageUrl: imageUrl,
        tgProviderId: selectedPG?.tgProviderId,
        accountId: selectedPG?.pgAccountId,
      });

      const data = await response.data;
      if (data.status !== "RS_OK") {
        toast.error(data.errorDescription);
      } else {
        closeDialog();
        if (activeWallet === "POINTS") {
          toast.success(t("transactions.messages.transactionSaved"))
        } else {
          toast.success(t("transactions.messages.transactionSavedSwitchWallet"));
        }
      }
    } catch (error) {
      console.log("There was a problem with the fetch operation.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: any) => {
    navigator.clipboard.writeText(text).then(() => {
    });
  }

  const handleLinkClick = (
    event: { preventDefault: () => void },
    link: string
  ) => {
    event?.preventDefault();
    // Redirect to the link
    window.open(link, "_blank");
  };

  const openUPIApps = (prefix?: string) => {
    let paymentWindow: Window | null = null;

    // console.log("sdfghj",  `${prefix}${paymentProps.upiIntent}`)
    if (prefix) {
      paymentWindow = window.open(
        `${prefix}${paymentProps.upiIntent}`,
        "_blank"
      );
    } else {
      // eslint-disable-next-line no-unused-vars
      paymentWindow = window.open(paymentProps.upiIntent, "cpgFrame");
    }
  };

  const renderUpiPaymentLinks = () => (
    <div className="flex justify-between items-center my-4">
      <div id="gpayLink" onClick={(e) => openUPIApps("tez://upi/")}>
        <img className="h-[60px] w-[60px]" src={gpayImage} alt="GPay" />
      </div>
      {/* <div id="phonepeLink" onClick={(e) => openUPIApps("phonepe://")}>
        <img className="h-[60px] w-[85px]" src={phonepeImage} alt="PhonePe" />
      </div> */}
      <div id="paytmLink" onClick={(e) => openUPIApps("paytmmp://")}>
        <img className="h-[60px] w-[60px]" src={paytmImage} alt="Paytm" />
      </div>
    </div>
  );

  const renderPaymentForm = (depositType: string) => (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(depositType);
        }}
        className="mt-4 w-full max-w-md mx-auto"
      >
        <div className="mb-4">
          <label htmlFor="screenshotUpload" className={`block text-sm ${pageTextColorClass} mb-1`}>
            {t("wallet.paymentScreenshot")} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="file"
              id="screenshotUpload"
              accept="image/*"
              onChange={handleScreenshotChange}
              className="hidden"
            />
            <label
              htmlFor="screenshotUpload"
              className={`p-2.5 w-full max-w-full text-sm ${buttonTextColorClass} bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] cursor-pointer flex items-center gap-2 ${screenshotFile ? 'justify-between' : 'justify-center'} ${ocrLoading ? 'opacity-50 cursor-not-allowed' : ''} overflow-hidden`}
            >
              <span className={screenshotFile ? "flex-1 truncate min-w-0 overflow-hidden text-ellipsis" : "whitespace-nowrap"}>
                {ocrLoading ? t("wallet.extractingUtr") : (screenshotFile ? screenshotFile.name : t("wallet.upload"))}
              </span>
              {screenshotFile && !ocrLoading && (
                <CancelIcon
                  className={`cursor-pointer hover:text-red-500 ${buttonTextColorClass} text-lg shrink-0 flex-shrink-0`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveScreenshot();
                  }}
                />
              )}
            </label>
          </div>
          {!screenshotFile && (
            <p className={`text-xs ${pageTextColorClass} mt-1`}>
              {t("wallet.pleaseUploadPaymentImage")}
            </p>
          )}
          {preview && (
            <div className="mt-3 flex justify-center">
              <div className="relative group">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full max-w-[300px] h-auto object-cover rounded-lg border border-border-color shadow-md"
                />
              </div>
            </div>
          )}
        </div>
        {screenshotFile && (
          <>
            <div className="mb-4">
              <label htmlFor="utrInput" className={`block mt-3 text-sm ${pageTextColorClass}`}>
                {t("transactions.utrNumber")}
              </label>
              <input
                type="number"
                className={`px-4 py-3 mt-2.5 w-full text-sm font-medium ${inputTextColorClass} placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem]`}
                id="utrInput"
                placeholder={t("transactions.enterUtrNumber")}
                value={utrNumber}
                onChange={(e) => handleUtrChange(e)}
                maxLength={12}
              />
            </div>
            <div className={`text-sm mb-2 ${isValidUtr ? 'text-green-500' : 'text-red-500'}`}>
              {!screenshotFile && utrNumber.length > 0 ? t("wallet.screenshotMandatory") :
            utrNumber.length === 0 ? t("transactions.utrNumberMustBe12Digits") :
              utrNumber.length < 12 ? t("transactions.enterMoreDigits").replace("{count}", String(12 - utrNumber.length)) :
                isValidUtr ? '' : t("transactions.utrNumberMustContainOnlyDigits")}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            className="p-2.5 w-full text-sm text-white whitespace-nowrap bg-red-600 rounded-lg border border-red-600 border-solid leading-[1.4rem]"
            onClick={closeDialog}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className={`p-2.5 w-full text-sm ${buttonTextColorClass} whitespace-nowrap bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${loading || ocrLoading || !screenshotFile ? "opacity-50" : ""
              }`}
            disabled={loading || ocrLoading || !isValidUtr || !screenshotFile}
          >
            <span className="submit-label">
              {loading ? t("common.processing") : ocrLoading ? t("wallet.extractingUtr") : t("common.submit")}
            </span>
          </button>
        </div>
      </form>
      <div className="h-[200px] p-4">
      </div>
    </>
  );

  const renderActiveTabContent = () => {
    if (activeTab === "upiPayment") {
      return (
        <div id="upiPayment" className="flex flex-col min-h-[30rem]">
          {
            paymentProps?.upiIntent !== "" ? renderUpiPaymentLinks() : <></>
          }
          <div className={`flex flex-col items-center ${pageTextColorClass} mt-4`}>
            <p className={`text-center text-sm ${pageTextColorClass}`}>
              {t("wallet.makePaymentToBelowUpiId")}
            </p>
            {ids.length > 0 ? ((
                <div className={`flex flex-col gap-2 mt-2 ${pageTextColorClass}`}>
                  {ids.map((id, index) => (
                    <div key={`${id}-${index}`} className={`flex items-center gap-2 ${pageTextColorClass}`}>
                      <span>{`UPI ${index + 1}: ${id}`}</span>
                      <ContentCopyIcon
                        className={`cursor-pointer hover:text-gray-800 ${pageTextColorClass}`}
                        onClick={() => copyToClipboard(id)}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>
          <div className="mt-auto mb-8">
            {renderPaymentForm("UPI")}
          </div>
        </div>
      );
    }

    if (activeTab === "qrPayment" && hasQrCodes) {
      return (
        <div id="qrPayment" className="flex flex-col min-h-[30rem]">
          <div className={`flex flex-col items-center ${pageTextColorClass}`}>
            <p className={`text-center text-sm ${pageTextColorClass}`}>
              {t("wallet.scanQrCodeUsingAnyUpiApplication")}
            </p>
            <div
              ref={qrScrollRef}
              className="m-3 flex w-full overflow-x-auto snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onScroll={(e) => {
                const target = e.currentTarget;
                if (!target.clientWidth) return;
                const index = Math.round(target.scrollLeft / target.clientWidth);
                if (index !== activeQrIndex) {
                  setActiveQrIndex(index);
                }
              }}
            >
              {qrCodes.map((qr, index) => (
                <div key={`qr-image-${index}`} className="w-full shrink-0 snap-center flex items-center justify-center">
                  <img
                    id={index === 0 ? "imageDisplay" : undefined}
                    alt={`QR Code ${index + 1}`}
                    className="w-4/5 max-w-[280px] h-auto object-contain"
                    src={qr}
                  />
                </div>
              ))}
            </div>
            {qrCodes.length > 1 && (
              <div className="mt-2 flex items-center justify-center gap-2">
                {qrCodes.map((_, index) => (
                  <button
                    key={`qr-dot-${index}`}
                    type="button"
                    onClick={() => {
                      setActiveQrIndex(index);
                      qrScrollRef.current?.scrollTo({
                        left: index * (qrScrollRef.current?.clientWidth || 0),
                        behavior: "smooth",
                      });
                    }}
                    aria-label={`QR ${index + 1}`}
                    className={`h-2.5 w-2.5 rounded-full transition-all ${index === activeQrIndex ? "bg-border-color scale-110" : "bg-gray-400/60"}`}
                  />
                ))}
              </div>
            )}
            <p className={`text-center text-sm ${pageTextColorClass}`}>
              {t("wallet.clickButtonAfterPayment")}
            </p>
          </div>
          <div className="mt-auto mb-8">
            {renderPaymentForm("UPI")}
          </div>
        </div>
      );
    }

    return (
      <div id="bankTransfer">
        <div className={`leading-10 ${pageTextColorClass}`}>
          <div id="actId" className={`flex items-center gap-2 ${pageTextColorClass}`}>
            <span>Account Number: {paymentProps.actId}</span>
            <ContentCopyIcon
              className={`cursor-pointer hover:text-gray-400 text-sm ${pageTextColorClass}`}
              style={{ fontSize: '18px' }}
              onClick={() => copyToClipboard(paymentProps.actId)}
            />
          </div>
          <div id="actName" className={`flex items-center gap-2 ${pageTextColorClass}`}>
            <span>{t("wallet.accountName")}: {paymentProps.actName}</span>
            <ContentCopyIcon
              className={`cursor-pointer hover:text-gray-400 text-sm ${pageTextColorClass}`}
              style={{ fontSize: '18px' }}
              onClick={() => copyToClipboard(paymentProps.actName)}
            />
          </div>
          <div id="ifsc" className={`flex items-center gap-2 ${pageTextColorClass}`}>
            <span>{t("wallet.ifscCode")}: {paymentProps.ifsc}</span>
            <ContentCopyIcon
              className={`cursor-pointer hover:text-gray-400 text-sm ${pageTextColorClass}`}
              style={{ fontSize: '18px' }}
              onClick={() => copyToClipboard(paymentProps.ifsc)}
            />
          </div>
          <p id="bankId" className={pageTextColorClass}>{t("wallet.bankId")}: {paymentProps.bankId}</p>
          <div id="bankName" className={`flex items-center gap-2 ${pageTextColorClass}`}>
            <span>{t("wallet.bankName")}: {paymentProps.bankName}</span>
            <ContentCopyIcon
              className={`cursor-pointer hover:text-gray-400 text-sm ${pageTextColorClass}`}
              style={{ fontSize: '18px' }}
              onClick={() => copyToClipboard(paymentProps.bankName)}
            />
          </div>
        </div>
        {renderPaymentForm("BANK_TRANSFER")}
      </div>
    );
  };

  return (
    <div className={`flex flex-col items-center p-4 ${pageTextColorClass}`}>
      <nav className={`flex gap-2 justify-between self-center w-full text-sm text-center ${pageTextColorClass}`}>
        {hasQrCodes && (
          <div
            id="qrTab"
            className={`flex-1 flex flex-col px-2 ${pageTextColorClass} cursor-pointer`}
            onClick={() => setActiveTab("qrPayment")}
          >
            <div className="text-center">{t("wallet.qr")}</div>
            {activeTab === "qrPayment" && (
              <div className="shrink-0 mt-3 h-0.5 bg-border-color rounded-lg" />
            )}
          </div>
        )}
        <div
          id="upiTab"
          className={`flex-1 flex flex-col px-2 ${pageTextColorClass} cursor-pointer ${!paymentProps.isUpi ? "opacity-50 cursor-not-allowed" : ""
            }`}
          onClick={() => paymentProps.isUpi && setActiveTab("upiPayment")}
        >
          <div className="text-center">{t("wallet.upi")}</div>
          {activeTab === "upiPayment" && (
            <div className="shrink-0 mt-3 h-0.5 bg-border-color rounded-lg" />
          )}
        </div>
        <div
          id="bankTransferTab"
          className={`flex-1 flex flex-col px-2 ${pageTextColorClass} cursor-pointer ${!paymentProps.isBankTransfer ? "opacity-50 cursor-not-allowed" : ""
            }`}
          onClick={() =>
            paymentProps.isBankTransfer && setActiveTab("bankTransfer")
          }
        >
          <div className="text-center">{t("wallet.bankTransfer")}</div>
          {activeTab === "bankTransfer" && (
            <div className="shrink-0 mt-3 h-0.5 bg-border-color rounded-lg" />
          )}
        </div>
      </nav>
      <div className="flex items-center justify-center">
        <div className="min-w-[17rem]">
          {renderActiveTabContent()}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    activeWallet: state.userDetails?.activeWallet,
    userDetails: state.userDetails?.userDetails,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

export default connect(mapStateToProps)(PaymentDialog);
